/**
 * 左右 2 ペインを横並びに配置し、間のバー (スプリッター) をマウスドラッグで
 * 掴んで領域配分を変更できるレイアウトコンポーネント。
 *
 * 現場 WG 要望 (受け入れ条件):
 *   - 「コンテンツ一覧と地図が横並びで表示されること」
 *   - 「コンテンツ一覧と地図の並びを分割するバーはマウスで掴んで領域配分を切り替えられること」
 *
 * 実装方針:
 * - 左ペイン幅を「コンテナ全体に対するパーセント」で state 管理し、CSS の
 *   `flex-basis` に反映する (px 固定ではなくウィンドウ幅変化に追従させる)。
 * - `onMouseDown` でドラッグ開始し、`window` の mousemove / mouseup を購読する
 *   (ポインタがバー外へ出ても追従させるため)。既存の `MapPopup` / モーダルの
 *   ドラッグ実装と同じパターン。
 * - 片方のペインが潰れないよう `MIN_RATIO` / `MAX_RATIO` でクランプする。
 * - キーボード操作 (←→) でも幅を変えられるようにして、マウスが使えない場合にも配慮する。
 * - 狭幅時 (モバイル) は CSS 側 (`@media`) で縦積みにフォールバックする。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** 左ペインの最小/最大比率 (%)。どちらかのペインが潰れるのを防ぐ。 */
const MIN_RATIO = 20;
const MAX_RATIO = 80;
/** キーボード操作 1 回あたりの変化量 (%) */
const KEYBOARD_STEP = 5;

export interface SplitPaneProps {
  /** 左ペインに表示する要素 */
  left: React.ReactNode;
  /** 右ペインに表示する要素 */
  right: React.ReactNode;
  /**
   * 左ペインの初期幅 (コンテナ全体に対する %)。省略時は 50 (等分)。
   * `MIN_RATIO` 〜 `MAX_RATIO` にクランプされる。
   */
  initialLeftRatio?: number;
  /** ルート要素に付与する data-testid (省略可) */
  testId?: string;
  /** スプリッターバーの aria-label (省略時は「領域配分を変更」) */
  splitterAriaLabel?: string;
  /**
   * 親要素の高さいっぱいに広げるか (既定: false)。
   *
   * true の場合 `.split-pane--full` を付与し、CSS 側で `height: 100%` +
   * 各ペインを `overflow: hidden` にする。画面全体を使うレイアウト
   * (ヘッダー以外すべてを一覧 + 地図で占める) で使用する。
   */
  fullHeight?: boolean;
}

/** 比率を MIN_RATIO 〜 MAX_RATIO にクランプする */
function clampRatio(ratio: number): number {
  if (Number.isNaN(ratio)) return 50;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
}

export function SplitPane({
  left,
  right,
  initialLeftRatio = 50,
  testId,
  splitterAriaLabel = '領域配分を変更',
  fullHeight = false,
}: SplitPaneProps): React.JSX.Element {
  const [leftRatio, setLeftRatio] = useState<number>(() => clampRatio(initialLeftRatio));
  /** ドラッグ中フラグ。state にすると mousemove ごとに再購読が走るため ref で持つ。 */
  const draggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /** マウス X 座標をコンテナ内の比率 (%) に変換して state に反映する */
  const applyClientX = useCallback((clientX: number): void => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    setLeftRatio(clampRatio(ratio));
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // テキスト選択が走ってドラッグ操作が阻害されるのを防ぐ
    e.preventDefault();
    draggingRef.current = true;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!draggingRef.current) return;
      applyClientX(e.clientX);
    };
    const handleMouseUp = (): void => {
      draggingRef.current = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [applyClientX]);

  /** キーボード (←→) でも領域配分を変えられるようにする */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setLeftRatio((r) => clampRatio(r - KEYBOARD_STEP));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setLeftRatio((r) => clampRatio(r + KEYBOARD_STEP));
    }
  };

  const rootClassName = fullHeight ? 'split-pane split-pane--full' : 'split-pane';

  return (
    <div className={rootClassName} ref={containerRef} data-testid={testId}>
      <div
        className="split-pane__pane split-pane__pane--left"
        style={{ flexBasis: `${leftRatio}%` }}
      >
        {left}
      </div>
      {/*
        分割バー。WAI-ARIA の separator ロールを付与し、現在の比率を
        aria-valuenow で伝える (スクリーンリーダー / E2E から状態を確認できる)。
      */}
      <div
        className="split-pane__splitter"
        data-testid="split-pane-splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label={splitterAriaLabel}
        aria-valuenow={Math.round(leftRatio)}
        aria-valuemin={MIN_RATIO}
        aria-valuemax={MAX_RATIO}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
      >
        <span className="split-pane__splitter-grip" aria-hidden="true" />
      </div>
      <div
        className="split-pane__pane split-pane__pane--right"
        style={{ flexBasis: `${100 - leftRatio}%` }}
      >
        {right}
      </div>
    </div>
  );
}
